<template>
  <!-- Project pages: only Back link — title moves to bottom of the view -->
  <nav v-if="route.name === 'Project' && !globalNav && !hideNav" class="mainnav noselect">
    <a :class="{ 'nav-entering': !navReady }" @click.prevent="handleBack">Back</a>
  </nav>

  <!-- Diary entry pages: Back link to newer entry or diary root -->
  <nav v-else-if="route.name === 'DiaryEntry' && !globalNav && !hideNav" class="mainnav noselect">
    <router-link v-if="diaryPrevEntry" :to="'/diary/' + diaryPrevEntry.uid">Back</router-link>
    <router-link v-else to="/diary">Back</router-link>
  </nav>

  <!-- All other pages (or global nav mode): standard nav; fades when navigating away from index -->
  <nav v-else-if="!hideNav" class="mainnav noselect" :class="{ 'nav-fading': navFading && !globalNav }">
    <router-link to="/index" :class="{ active: workActive }">Work</router-link>
    <router-link to="/information" :class="{ active: route.path == '/information' }">Information</router-link>
    <router-link to="/diary" :class="{ active: route.path.startsWith('/diary') }">Diary</router-link>
  </nav>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { backRoute } from '@/composables/useBackRoute'
import { transitionCover, clearTransitionCover } from '@/composables/useTransitionCover'
import { navFading } from '@/composables/useNavFading'
import { projectFading } from '@/composables/useProjectFading'

const route = useRoute();
const router = useRouter();
const kql = useStore();
const workActive = computed(() => route.path === '/' || route.path.startsWith('/index'));
const globalNav = computed(() => kql.site?.content?.globalnav === 'true')
const hideNav = computed(() => kql.site?.content?.hidenav === 'true')

// Diary entry nav: resolve the newer adjacent entry (lower index in date-desc list)
const diarySiblings = computed(() => Object.values(kql.page?.siblings || {}));
const diaryCurrentIdx = computed(() => diarySiblings.value.findIndex(s => s.uid === route.params.slug));
const diaryPrevEntry = computed(() => diaryCurrentIdx.value > 0 ? diarySiblings.value[diaryCurrentIdx.value - 1] : null);

// Back link: hidden while overlay is active, fades in when it clears (mirrors textReady in Project.vue)
const navReady = ref(!transitionCover.value)
watch(transitionCover, val => {
  if (route.name !== 'Project') return
  navReady.value = !val
})
watch(() => route.name, name => {
  navReady.value = name !== 'Project' || !transitionCover.value
})

// Clicking Back: fade out Back link + project text, then navigate
const handleBack = () => {
  if (!navReady.value) return // already fading
  navReady.value = false
  projectFading.value = true
  setTimeout(() => { clearTransitionCover(); router.push(backRoute.value) }, 400)
}
</script>

<style>
.mainnav {
  position: fixed;
  top: var(--page-margin, 28px);
  left: var(--page-margin, 32px);
  z-index: 10;
  display: flex;
  gap: 27px;
  transition: opacity 400ms ease;
}

.mainnav.nav-fading {
  opacity: 0;
}

/* Nav links: upright by default, italic only when active */
.mainnav a {
  font-style: normal;
  cursor: pointer;
  transition: opacity 400ms ease;
}

.mainnav a.active {
  font-style: italic;
}

/* Back link: starts hidden when entering project; fades in when nav-entering is removed */
.mainnav a.nav-entering {
  opacity: 0;
}

.sitetitle {
  position: fixed;
  top: var(--page-margin, 28px);
  right: var(--page-margin, 32px);
  z-index: 10;
  font-style: normal;
}

@media only screen and (max-width: 700px) {
  .mainnav {
    top: var(--page-margin, 16px);
    left: var(--page-margin, 16px);
    gap: 22px;
  }

  .sitetitle {
    display: none;
  }
}
</style>
